import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { mediaUrl } from "@/lib/api";
import PaymentBadges from "@/components/PaymentBadges";
import { Trash2 } from "lucide-react";

export default function CartPage() {
  const { items, removeItem } = useCart();
  const { formatValue, convert, shippingFor, shipping } = useCurrency();
  const shipCfg = shipping?.chf || {};
  const navigate = useNavigate();

  const subtotal = items.reduce((s, i) => s + convert(i.price) * i.quantity, 0);
  const shippingFee = shippingFor(subtotal);
  const total = subtotal + shippingFee;

  const checkout = () => navigate("/checkout");

  if (items.length === 0)
    return (
      <div className="pt-40 pb-40 text-center px-6">
        <h1 className="font-serif-display text-4xl lg:text-5xl font-light mb-6">Your bag is empty</h1>
        <Link
          to="/collection"
          data-testid="empty-cart-shop-btn"
          className="inline-block border border-white text-white px-10 py-4 font-mono-label text-xs uppercase tracking-luxe hover-invert hover:bg-white hover:text-black"
        >
          Continue Shopping
        </Link>
      </div>
    );

  return (
    <div className="pt-28 lg:pt-36 pb-24 max-w-[1400px] mx-auto px-6 lg:px-12">
      <h1 className="font-serif-display text-5xl lg:text-6xl font-light uppercase tracking-tight mb-14">Your Bag</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-14">
        <div className="lg:col-span-2 divide-y divide-white/10 border-t border-white/10">
          {items.map((i) => (
            <div key={i.product_id} data-testid="cart-page-item" className="flex gap-6 py-8">
              <Link to={`/product/${i.product_id}`} className="w-28 h-36 bg-neutral-900 shrink-0 overflow-hidden">
                {i.image && <img src={mediaUrl(i.image)} alt={i.name} className="w-full h-full object-cover" />}
              </Link>
              <div className="flex-1 flex flex-col">
                <div className="flex justify-between gap-4">
                  <div>
                    <h3 className="text-white text-lg">{i.name}</h3>
                    <p className="font-mono-label text-xs text-white/40 mt-1 uppercase tracking-luxe">Size {i.size}</p>
                  </div>
                  <span className="font-mono-label text-white">{formatValue(convert(i.price))}</span>
                </div>
                <div className="mt-auto flex items-center justify-between">
                  <span className="font-mono-label text-[10px] uppercase tracking-luxe text-white/30">One-of-one · Qty 1</span>
                  <button data-testid="page-item-remove" onClick={() => removeItem(i.product_id)} className="flex items-center gap-2 text-white/40 hover:text-white font-mono-label text-xs uppercase tracking-luxe">
                    <Trash2 size={14} /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:sticky lg:top-28 lg:self-start border border-white/10 p-8">
          <h2 className="font-mono-label text-xs uppercase tracking-luxe text-white/50 mb-8">Order Summary</h2>
          <div className="space-y-4 text-sm">
            <div className="flex justify-between text-white/70">
              <span>Subtotal</span>
              <span data-testid="page-subtotal" className="font-mono-label text-white">{formatValue(subtotal)}</span>
            </div>
            <div className="flex justify-between text-white/70">
              <span>Shipping{shipCfg.label ? ` · ${shipCfg.label}` : ""}</span>
              <span data-testid="page-shipping" className="font-mono-label">
                {shippingFee === 0 ? "Free" : formatValue(shippingFee)}
              </span>
            </div>
          </div>
          <div className="flex justify-between text-white text-lg mt-8 pt-6 border-t border-white/10">
            <span>Total</span>
            <span data-testid="page-total" className="font-mono-label">{formatValue(total)}</span>
          </div>
          <button
            data-testid="checkout-btn"
            onClick={checkout}
            className="w-full mt-8 bg-white text-black py-4 font-mono-label text-xs uppercase tracking-luxe hover-invert hover:bg-transparent hover:text-white border border-white"
          >
            Proceed to Checkout
          </button>
          <PaymentBadges className="mt-5" />
          <p className="mt-4 text-center font-mono-label text-[10px] uppercase tracking-luxe text-white/30">
            Secured by Stripe · Test card 4242 4242 4242 4242
          </p>
        </div>
      </div>
    </div>
  );
}
